import { parseDateLiteral } from '@/utils/chartAxisUtils';

export const clampWindow = (
  totalPoints: number,
  startIndex: number,
  endIndex: number,
): { startIndex: number; endIndex: number } => {
  if (totalPoints <= 0) {
    return { startIndex: 0, endIndex: 0 };
  }

  const maxIndex = totalPoints - 1;
  const boundedStart = Math.max(0, Math.min(startIndex, maxIndex));
  const boundedEnd = Math.max(0, Math.min(endIndex, maxIndex));

  if (boundedEnd < boundedStart) {
    return { startIndex: boundedEnd, endIndex: boundedStart };
  }

  return { startIndex: boundedStart, endIndex: boundedEnd };
};

export const getDefaultWindow = (
  totalPoints: number,
  preferredVisiblePoints?: number,
): { startIndex: number; endIndex: number } => {
  if (totalPoints <= 0) {
    return { startIndex: 0, endIndex: 0 };
  }

  const endIndex = totalPoints - 1;
  if (!preferredVisiblePoints || preferredVisiblePoints >= totalPoints) {
    return { startIndex: 0, endIndex };
  }

  return {
    startIndex: Math.max(0, totalPoints - preferredVisiblePoints),
    endIndex,
  };
};

export const getDefaultPreviousYearWindowFromLabels = (
  labels: string[],
): { startIndex: number; endIndex: number } => {
  if (labels.length <= 0) {
    return { startIndex: 0, endIndex: 0 };
  }

  const endIndex = labels.length - 1;
  const dateFrom = new Date();
  dateFrom.setFullYear(dateFrom.getFullYear() - 1);

  const firstIndexInWindow = labels.findIndex((label) => {
    const parsed = parseDateLiteral(String(label));
    if (isNaN(parsed.getTime())) return false;
    return parsed.getTime() >= dateFrom.getTime();
  });

  if (firstIndexInWindow === -1) {
    return getDefaultWindow(labels.length);
  }

  return {
    startIndex: firstIndexInWindow,
    endIndex,
  };
};

export const sliceLabelsAndDatasets = <T extends { data: (number | null)[] }>(
  labels: string[],
  datasets: T[],
  startIndex: number,
  endIndex: number,
): { labels: string[]; datasets: T[] } => {
  const window = clampWindow(labels.length, startIndex, endIndex);
  const slicedLabels = labels.slice(window.startIndex, window.endIndex + 1);
  const slicedDatasets = datasets.map((dataset) => ({
    ...dataset,
    data: dataset.data.slice(window.startIndex, window.endIndex + 1),
  } as T));

  return { labels: slicedLabels, datasets: slicedDatasets };
};

export const getOneYearDateRange = (): { dateFrom: string; dateTo: string } => {
  const toDate = new Date();
  const fromDate = new Date(toDate);
  fromDate.setFullYear(fromDate.getFullYear() - 1);

  return {
    dateFrom: fromDate.toISOString().split('T')[0],
    dateTo: toDate.toISOString().split('T')[0],
  };
};